/* eslint-disable react-refresh/only-export-components -- hooks are the context consumers for this provider */
import {
  type FC,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import { selectAvailableHomeRoles, selectCurrentUserId } from "store/selectors";
import { localStateActions } from "store/slices/local";
import { usePromiseEffect } from "store/store-hooks";
import { getHomeRoleFromPath, resolveHomeRole } from "utils/home-role";
import { useLocationPathname } from "utils/routing";
import { HomeRole } from "utils/urls";

type HomeRoleContextValue = {
  isHomeRolesPending: boolean;
  homeRole: HomeRole | null;
};

const HomeRoleContext = createContext<HomeRoleContextValue>({
  isHomeRolesPending: false,
  homeRole: null,
});

export const useIsHomeRolesPending = () => useContext(HomeRoleContext).isHomeRolesPending;

export const useHomeRole = (): HomeRole | null => useContext(HomeRoleContext).homeRole;

/**
 * Fetches enrollments/orgs for home role availability, exposes pending + homeRole via
 * context, and persists lastHomeRole. Wrap authenticated app shell (Observers + routes).
 *
 * @param initiallyHydrated - Tests: skip the pending gate when enrollments are seeded in the store.
 */
export const HomeRoleProvider: FC<{ children: ReactNode; initiallyHydrated?: boolean }> = ({
  children,
  initiallyHydrated = false,
}) => {
  const dispatch = useAppDispatch();
  const pathname = useLocationPathname();
  const userId = useAppSelector(selectCurrentUserId);
  const availableRoles = useAppSelector(selectAvailableHomeRoles);
  const [hydratedForUser, setHydratedForUser] = useState(initiallyHydrated);

  usePromiseEffect(() => {
    if (!userId) {
      setHydratedForUser(false);
      return Promise.resolve(undefined);
    }

    return Promise.all([
      storeApi.orgs.list(),
      storeApi.section_users.list({ user_id: userId }),
      storeApi.org_users.list({ user_id: userId }),
      storeApi.course_users.list({ user_id: userId }),
      storeApi.course_grouping_users.list({ user_id: userId }),
    ]).finally(() => {
      setHydratedForUser(true);
    });
  }, [userId]);

  const isHomeRolesPending = !hydratedForUser;
  const pathRole = getHomeRoleFromPath(pathname);
  const homeRole = resolveHomeRole(pathRole, availableRoles, isHomeRolesPending);
  const value = useMemo(() => ({ isHomeRolesPending, homeRole }), [isHomeRolesPending, homeRole]);

  const accessibleHomeRole = pathRole && availableRoles.includes(pathRole) ? pathRole : null;

  useEffect(() => {
    if (isHomeRolesPending || !accessibleHomeRole) {
      return;
    }
    dispatch(localStateActions.setLastHomeRole(accessibleHomeRole));
  }, [accessibleHomeRole, isHomeRolesPending, dispatch]);

  return <HomeRoleContext.Provider value={value}>{children}</HomeRoleContext.Provider>;
};
